/**
 * rrweb session recorder (INT-0035) — records every admin session (DOM
 * mutations, masked inputs) and ships gzip-bound chunks to
 * POST /api/v1/adminUsage/recordings for the owner-only replay surface.
 *
 * Design rules (mirrors usageTracker.ts):
 *  - FAIL-SILENT, always: recording must never affect the admin. Every path
 *    is try/caught; a dead endpoint drops chunks; a 401 goes quiet for the
 *    rest of the page lifetime.
 *  - PRIVACY: `maskAllInputs` — what admins type is replaced with ***** at
 *    record time and never reaches the server. Cross-origin iframes (Stripe
 *    onboarding/checkout) are not recordable by design.
 *  - Identity is stamped server-side; the client sends only
 *    sessionId/seq/page/events. The sessionId is the SAME one usageTracker
 *    uses, so recordings join the beacon click data.
 *  - Disabled under test (jsdom + rrweb don't mix, and suites must not
 *    beacon).
 */
const ENDPOINT = `${process.env.API_URL}/api/v1/adminUsage/recordings`;
const FLUSH_MS = 10_000;
const SESSION_KEY = 'staty_usage_session'; // shared with usageTracker
const MAX_BUFFER_EVENTS = 4_000; // runaway guard; a flush normally carries far fewer

// Idle segmentation (owner request 2026-10-07): a tab left open used to keep
// one "session" alive for the whole tab lifetime — the 5-min keyframe
// snapshots emit even with nobody there, producing 16-hour replay rows. Now:
// 2 minutes without pointer/keyboard/touch/scroll/visibility activity ENDS
// the recording segment (recorder stopped, buffer flushed); the next
// activity starts a fresh segment under `<sessionId>#<n>`, which lists as
// its own row with an honest duration.
const IDLE_MS = 2 * 60 * 1000;
const IDLE_CHECK_MS = 15_000;
const ACTIVITY_THROTTLE_MS = 5_000;

let started = false;
let buffer: unknown[] = [];
let seq = 0;
let sessionDead = false;
let segment = 1;
let idle = false;
let lastActivity = 0;
let stopRecording: (() => void) | null = null;
let recordFn: ((options: Record<string, unknown>) => (() => void) | undefined) | null = null;

function sessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return 'no-storage';
  }
}

// Chrome hard-caps `keepalive` fetches AND sendBeacon at ~64KB of body —
// rrweb's full-snapshot chunks are far bigger (hundreds of KB), and an
// over-cap request is rejected by the BROWSER before it leaves (this
// silently dropped every real chunk until caught 2026-10-05). So: interval
// flushes use a plain fetch (the page is alive; no keepalive needed), and
// the unload-time flush uses sendBeacon only when the payload fits.
const BEACON_MAX_BYTES = 60_000;

// The recordings route accepts 8MB per chunk (apps/api .../recordingLimits.ts);
// stay well under it. Measured in UTF-8 BYTES, which is what the server
// counts — `string.length` undercounts any non-ASCII text in the DOM.
const MAX_PAYLOAD_BYTES = 6_000_000;

/** UTF-8 byte size of a JSON string (what body-parser's limit measures). */
export function payloadBytes(json: string): number {
  try {
    return new Blob([json]).size;
  } catch {
    return json.length;
  }
}

/**
 * Split one flush into upload-sized chunks: halve while the wrapped payload
 * is over `maxBytes`. A SINGLE event over the cap on its own (a full-page
 * snapshot of a very large DOM) cannot be split and is DROPPED rather than
 * sent to a guaranteed 413 — before 2026-10-30 it went out anyway: three
 * 2.9MB snapshots against the then-2MB server cap, each one a Sentry error
 * (NODE-EXPRESS-9R) and a replay without its keyframe. Order is preserved.
 */
export function splitForUpload(
  events: unknown[],
  maxBytes: number,
  measure: (events: unknown[]) => number,
): unknown[][] {
  if (!events.length) return [];
  if (measure(events) <= maxBytes) return [events];
  if (events.length === 1) return [];
  const mid = Math.ceil(events.length / 2);
  return [
    ...splitForUpload(events.slice(0, mid), maxBytes, measure),
    ...splitForUpload(events.slice(mid), maxBytes, measure),
  ];
}

function flush(isUnload = false): void {
  try {
    if (sessionDead || !buffer.length) return;
    const events = buffer;
    buffer = [];
    sendEvents(events, isUnload);
  } catch {
    /* fail-silent */
  }
}

function sendEvents(events: unknown[], isUnload: boolean): void {
  try {
    if (sessionDead || !events.length) return;
    // Segment 1 keeps the bare sessionId (joins the beacon click data);
    // post-idle segments get #2, #3… and list as their own replay rows.
    const sid = segment > 1 ? `${sessionId()}#${segment}` : sessionId();
    const page = window.location.pathname;
    const wrap = (evs: unknown[], n: number) => JSON.stringify({ sessionId: sid, seq: n, page, events: evs });
    const chunks = splitForUpload(events, MAX_PAYLOAD_BYTES, (evs) => payloadBytes(wrap(evs, seq)));
    for (const chunk of chunks) postPayload(wrap(chunk, seq++), isUnload); // each chunk claims its own seq
  } catch {
    /* fail-silent */
  }
}

function postPayload(payload: string, isUnload: boolean): void {
  try {
    if (isUnload) {
      const blob = new Blob([payload], { type: 'application/json' });
      if (navigator.sendBeacon && blob.size <= BEACON_MAX_BYTES) {
        navigator.sendBeacon(ENDPOINT, blob);
        return;
      }
      // Oversized tail on unload: best-effort plain fetch — it may be
      // cancelled by navigation; losing the last few seconds is acceptable.
    }
    fetch(ENDPOINT, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
    })
      .then((res) => {
        // 401: session gone. 404: the API doesn't have the recordings route
        // (stale deploy) — keep retrying and every 10s flush becomes a
        // server-side 404 that pages the owner via Staty Bug Alert (seen
        // live 2026-10-05). Either way: go quiet for this page lifetime.
        if (res.status === 401 || res.status === 404) {
          sessionDead = true;
          buffer = [];
        }
      })
      .catch(() => {});
  } catch {
    /* fail-silent */
  }
}


/**
 * Idempotent global start — safe to call from multiple layouts. rrweb is
 * imported DYNAMICALLY so test environments (and any failed chunk load)
 * never see it — recording silently doesn't start, the admin is unaffected.
 */
export function startSessionRecorder(): void {
  try {
    if (started || typeof window === 'undefined') return;
    if (process.env.NODE_ENV === 'test') return;
    started = true;

    import('rrweb')
      .then(({ record }) => {
        recordFn = record as typeof recordFn;
        lastActivity = Date.now();
        beginSegmentRecording();

        setInterval(() => flush(false), FLUSH_MS);
        setInterval(checkIdle, IDLE_CHECK_MS);

        // Any human input counts as activity. Throttled: mousemove fires
        // constantly; stamping every ACTIVITY_THROTTLE_MS is plenty for a
        // 2-minute threshold.
        let lastStamp = 0;
        const onActivity = () => {
          try {
            const now = Date.now();
            if (now - lastStamp < ACTIVITY_THROTTLE_MS) return;
            lastStamp = now;
            lastActivity = now;
            if (idle) resumeFromIdle();
          } catch {
            /* fail-silent */
          }
        };
        // NOT 'scroll': it also fires for PROGRAMMATIC scrolling, so a page
        // that auto-scrolls would keep an unattended tab "active" forever
        // (caught during live verification). wheel/touchstart cover human
        // scrolling; mousedown covers scrollbar drags.
        for (const evt of ['mousemove', 'mousedown', 'keydown', 'touchstart', 'wheel']) {
          window.addEventListener(evt, onActivity, { capture: true, passive: true });
        }

        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'hidden') {
            flush(true);
          } else {
            onActivity();
          }
        });
        window.addEventListener('pagehide', () => flush(true));
      })
      .catch(() => {});
  } catch {
    /* fail-silent */
  }
}

function beginSegmentRecording(): void {
  try {
    if (!recordFn) return;
    stopRecording =
      recordFn({
        emit(event: unknown) {
          try {
            if (sessionDead || idle) return;
            if (buffer.length >= MAX_BUFFER_EVENTS) return;
            buffer.push(event);
          } catch {
            /* fail-silent */
          }
        },
        maskAllInputs: true,
        // Checkout a fresh full snapshot periodically so replays of long
        // segments can start from a recent keyframe instead of old DOM.
        checkoutEveryNms: 5 * 60 * 1000,
      }) || null;
  } catch {
    stopRecording = null;
  }
}

function checkIdle(): void {
  try {
    if (idle || sessionDead) return;
    if (Date.now() - lastActivity < IDLE_MS) return;
    idle = true;
    // End the segment: stop the recorder entirely (keyframe snapshots would
    // otherwise keep extending the recording) and ship what's buffered.
    if (stopRecording) {
      try {
        stopRecording();
      } catch {
        /* fail-silent */
      }
      stopRecording = null;
    }
    flush(false);
  } catch {
    /* fail-silent */
  }
}

function resumeFromIdle(): void {
  try {
    idle = false;
    segment += 1;
    seq = 0; // fresh segment, fresh chunk ordering (server orders by firstTs anyway)
    // A fresh record() emits a new full snapshot, so the new segment replays
    // from a clean keyframe.
    beginSegmentRecording();
  } catch {
    /* fail-silent */
  }
}
